import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import { POLL_DETAIL_KEYS } from '@/api/usePoll';
import { POLL_KEYS } from '@/api/usePolls';

import type { Comment, CreateCommentPayload, UpdateCommentPayload } from '@/types/poll';

const KEYS = {
    all: ['comments'] as const,
    list: (pollSlug: string) => [...KEYS.all, 'list', pollSlug] as const,
};

export { KEYS as COMMENT_KEYS };

interface PaginatedComments {
    results: Comment[];
    count: number;
    next: string | null;
}

export function useComments(pollSlug: string) {
    return useInfiniteQuery({
        queryKey: KEYS.list(pollSlug),
        queryFn: async ({ pageParam = 0 }) => {
            const { data } = await api.get<PaginatedComments>(
                API_ROUTES.pollComments(pollSlug),
                { params: { limit: 20, offset: pageParam } }
            );
            return data;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            if (!lastPage.next) return undefined;
            try {
                const url = new URL(lastPage.next);
                const offset = url.searchParams.get('offset');
                return offset ? parseInt(offset, 10) : undefined;
            } catch {
                return undefined;
            }
        },
        enabled: !!pollSlug,
    });
}

export function useCreateComment(pollSlug: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: CreateCommentPayload) => {
            const { data } = await api.post<Comment>(API_ROUTES.pollComments(pollSlug), payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KEYS.list(pollSlug) });
            queryClient.invalidateQueries({ queryKey: POLL_DETAIL_KEYS.detail(pollSlug) });
            queryClient.invalidateQueries({ queryKey: POLL_KEYS.all });
            toast.success('Comentario publicado');
        },
        onError: () => {
            toast.error('No se pudo publicar el comentario. Intenta de nuevo.');
        },
    });
}

export function useUpdateComment(pollSlug: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ commentId, payload }: { commentId: number; payload: UpdateCommentPayload }) => {
            const { data } = await api.patch<Comment>(API_ROUTES.commentDetail(commentId), payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KEYS.list(pollSlug) });
            toast.success('Comentario actualizado');
        },
        onError: () => {
            toast.error('No se pudo actualizar el comentario. Intenta de nuevo.');
        },
    });
}

export function useDeleteComment(pollSlug: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (commentId: number) => {
            await api.delete(API_ROUTES.commentDetail(commentId));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KEYS.list(pollSlug) });
            queryClient.invalidateQueries({ queryKey: POLL_DETAIL_KEYS.detail(pollSlug) });
            queryClient.invalidateQueries({ queryKey: POLL_KEYS.all });
            toast.success('Comentario eliminado');
        },
    });
}
